import { format, isThisYear, isToday, isYesterday } from 'date-fns';
import type { ChannelData, ChannelMessageData, ChatUser } from '@/api/services/channels.service';

/** Shared by message search and saved messages, which list messages the same way. */

export function fullName(user: ChatUser): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email;
}

export function senderOf(message: Pick<ChannelMessageData, 'senderId'>): ChatUser | null {
  return typeof message.senderId === 'string' ? null : message.senderId;
}

export function isDirectKind(kind: ChannelData['kind']): boolean {
  return kind === 'direct' || kind === 'group_dm';
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Words worth highlighting, ignoring Mongo text-search syntax (quotes, `-`). */
export function searchTerms(query: string, minLength = 2): string[] {
  return query
    .replace(/["]/g, ' ')
    .split(/\s+/)
    .map((term) => term.replace(/^-/, ''))
    .filter((term) => term.length >= minLength);
}

/** Message bodies are stored as lightweight markdown (and polls as JSON). */
export function toPlainText(content: string): string {
  const poll = content.match(/^\s*\[POLL:([\s\S]*)\]\s*$/);
  if (poll) {
    try {
      const question = (JSON.parse(poll[1]) as { question?: string }).question;
      return question ? `Poll: ${question}` : 'Poll';
    } catch {
      return 'Poll';
    }
  }
  return content
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*|__|~~|`/g, '')
    .replace(/(^|\s)[*_](\S(?:[^*_]*\S)?)[*_](?=\s|$|[.,!?])/g, '$1$2')
    .replace(/^\s*(?:>|[-*+]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Start the excerpt near the first match so it survives the two-line clamp. */
export function excerptAround(text: string, terms: string[]): string {
  const lower = text.toLowerCase();
  const first = terms
    .map((term) => lower.indexOf(term.toLowerCase()))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0];
  if (first === undefined || first < 80) return text;
  const start = text.lastIndexOf(' ', first - 40);
  return `…${text.slice(start > 0 ? start + 1 : first - 40)}`;
}

export function formatResultDate(value: string): string {
  const date = new Date(value);
  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
    date
  );
  if (isToday(date)) return time;
  if (isYesterday(date)) return `Yesterday, ${time}`;
  if (isThisYear(date)) return `${format(date, 'MMM d')}, ${time}`;
  return format(date, 'MMM d, yyyy');
}

export function formatFullDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'short' }).format(
    new Date(value)
  );
}

export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
